import { Link, NavLink } from 'react-router-dom';
import { Users, FolderKanban, CalendarDays, TrendingUp, FileText, Loader2, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/App';
import { ASSETS } from '../constants/site';

const NAV = [
  { to: '/admin', end: true, icon: Users, label: 'Team & Roles' },
  { to: '/admin/projects', icon: FolderKanban, label: 'Projects' },
  { to: '/admin/crm', icon: TrendingUp, label: 'Sales CRM' },
  { to: '/admin/calendar', icon: CalendarDays, label: 'Calendar' },
  { to: '/admin/settings', icon: FileText, label: 'Site & Twilio' },
];

export default function AdminLayout({ title, subtitle, children, testId = 'admin-page' }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center">
        <Loader2 size={22} className="text-amber-400 animate-spin" />
      </div>
    );
  }
  if (!user) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center px-6">
        <div className="gold-ring rounded-3xl bg-[#0F1C33]/70 p-10 max-w-md w-full text-center">
          <img src={ASSETS.icon} alt="GSLAB" className="h-16 w-16 rounded-xl mx-auto mb-6" />
          <h1 className="font-display font-bold text-2xl mb-3">Staff only</h1>
          <p className="text-sm text-slate-400 mb-8">Sign in with a GSLAB staff account.</p>
          <Link to="/login" data-testid="admin-login-link" className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#F3C64F] text-[#050B14] font-bold rounded-full px-8 py-3">
            Sign in
          </Link>
        </div>
      </div>
    );
  }
  const isAdmin = user.role === 'admin';
  const isStaff = isAdmin || user.role === 'developer' || user.role === 'sales';
  if (!isStaff) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center px-6">
        <div className="gold-ring rounded-3xl bg-[#0F1C33]/70 p-10 max-w-md w-full text-center">
          <img src={ASSETS.icon} alt="GSLAB" className="h-16 w-16 rounded-xl mx-auto mb-6" />
          <h1 className="font-display font-bold text-2xl mb-3">Staff only</h1>
          <p className="text-sm text-slate-400 mb-8">Signed in as {user.email} (customer) — this area is for the GSLAB team.</p>
          <Link to="/account" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300 transition-colors">
            <ArrowLeft size={13} /> Back to your portal
          </Link>
        </div>
      </div>
    );
  }
  const visibleNav = NAV.filter((n) => isAdmin || n.to === '/admin/projects' || n.to === '/admin/crm' || n.to === '/admin/calendar');

  return (
    <div data-testid={testId} className="min-h-screen bg-[#050B14] pt-[72px]">
      <div className="border-b border-slate-800 bg-[#0B1528]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 flex items-center gap-1 overflow-x-auto py-3">
          {visibleNav.map(({ to, end, icon: Icon, label }) => (
            <NavLink key={to} to={to} end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s|&/g, '-').replace(/-+/g, '-')}`}
              className={({ isActive }) => `inline-flex items-center gap-2 whitespace-nowrap font-mono-tech text-[11px] tracking-widest uppercase rounded-full px-4 py-2 transition-colors ${
                isActive ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40' : 'text-slate-400 border border-transparent hover:text-amber-200'}`}>
              <Icon size={13} /> {label}
            </NavLink>
          ))}
          <Link to="/account" className="ml-auto whitespace-nowrap text-xs text-slate-500 hover:text-amber-300 transition-colors pl-4">← Portal</Link>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <div className="mb-8">
          <h1 className="font-display font-bold text-2xl sm:text-3xl">{title}</h1>
          {subtitle && <p className="text-sm text-slate-500 mt-2">{subtitle}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}
